import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import SessionCard from "../components/SessionCard";
import { useBackdrop } from "../lib/backdrops";
import { Flame, Sparkles, Wind, Moon, Brain, Loader2, Plus, Lock, Zap, ArrowRight } from "lucide-react";

const FOCUS = [
  { id: "Game Prep", icon: Brain, tint: "from-royal to-turq" },
  { id: "Focus", icon: Sparkles, tint: "from-turq to-royal" },
  { id: "Calm", icon: Wind, tint: "from-royal/90 to-cyan-500" },
  { id: "Sleep", icon: Moon, tint: "from-navy to-royal" },
];

export default function Home() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/library");
    setItems(data.items);
  }, []);
  useEffect(() => { load(); }, [load]);

  const hero = items.find((i) => i.kind === "personalized" && !i.pending);
  const pendingCount = items.filter((i) => i.pending).length;
  const affirmation = items.find((i) => i.type === "affirmation");
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const first = (user?.name || "Athlete").split(" ")[0];
  const credits = Math.max(0, user?.credits || 0);
  const teaserBackdrop = useBackdrop("tomorrow");

  const generate = async (focus) => {
    setBusy(true);
    try {
      await api.post("/sessions/generate", { focus });
      await Promise.all([load(), refresh()]);
      toast.success(`New ${focus} session added to your library`);
    } catch (err) {
      if (err?.response?.status === 402) { toast.info("You're out of sessions — grab a pack!"); navigate("/paywall"); }
      else toast.error("Could not generate session");
    } finally { setBusy(false); }
  };

  return (
    <div className="mm-glow relative flex-1 overflow-y-auto mm-scroll bg-white" data-testid="home-page">
      <div className="relative z-10 px-5 pt-6 pb-8">
        <div className="flex items-center justify-between">
          <Logo size={36} />
          <div className="flex items-center gap-1.5 bg-orange-50 border border-orange-200 text-orange-600 rounded-full px-3 py-1.5" data-testid="streak-badge">
            <Flame size={16} className="fill-orange-500 text-orange-500" />
            <span className="font-bold text-sm">{user?.journal_streak || 0}</span>
          </div>
        </div>

        <div className="mt-6">
          <p className="text-slate-500 text-sm font-medium">{greet},</p>
          <h1 className="font-serif text-3xl font-bold text-navy">{first} 👋</h1>
        </div>

        {affirmation && (
          <div className="mt-5 rounded-2xl bg-gradient-to-br from-slate-50 to-blue-50/50 border border-slate-200 p-4" data-testid="daily-affirmation">
            <div className="text-[11px] font-bold uppercase tracking-wider text-royal mb-1">Today's affirmation</div>
            <p className="font-serif text-lg text-navy italic leading-snug">"{affirmation.title}"</p>
          </div>
        )}

        <div className="mt-6">
          {hero ? <SessionCard item={hero} hero /> : pendingCount > 0 ? (
            <div className="rounded-3xl bg-navy text-white p-6" data-testid="finalizing-hero">
              <div className="flex items-center gap-2 text-turq text-[11px] font-bold uppercase tracking-wider"><Loader2 size={14} className="animate-spin" /> Coach is finalizing</div>
              <div className="font-serif text-2xl font-bold mt-2 leading-tight">Your custom session is on its way</div>
              <p className="text-white/70 text-sm mt-2">Available within 24 hours or less. Your general content below is ready right now.</p>
            </div>
          ) : (
            <div className="rounded-3xl bg-navy text-white p-6 text-center">
              <p className="text-white/80">No session yet — create your first one below.</p>
            </div>
          )}
        </div>

        {hero && pendingCount > 0 && (
          <div className="mt-3 flex items-center gap-2 text-xs font-semibold text-slate-500 px-1" data-testid="finalizing-note">
            <Loader2 size={13} className="animate-spin text-royal" /> {pendingCount} custom session{pendingCount === 1 ? "" : "s"} being finalized by Coach · within 24 hours
          </div>
        )}

        {credits === 0 && (
          <button data-testid="locked-next-session" onClick={() => navigate("/paywall")}
            className="mt-4 w-full relative overflow-hidden rounded-3xl text-left active:scale-[0.99] transition shadow-lg shadow-navy/20">
            <img src={teaserBackdrop.file} alt="" className="absolute inset-0 w-full h-full object-cover blur-[2px] scale-105" />
            <div className="absolute inset-0 bg-navy/70" />
            <div className="relative p-5 text-white">
              <div className="text-[11px] font-bold uppercase tracking-wider text-turq">Tomorrow's session</div>
              <div className="font-serif text-xl font-bold mt-1 blur-[3px] select-none opacity-80">Clutch composure, late innings</div>
              <div className="text-sm text-white/70 mt-1">Personalized for {first} · 3 min · {user?.profile?.guide?.replace("_", " ") || "your guide"}</div>
              <span className="mt-4 inline-flex items-center gap-2 bg-white text-navy text-sm font-bold px-4 py-2.5 rounded-full shadow-lg">
                <Lock size={15} /> Unlock your next session
              </span>
            </div>
          </button>
        )}

        {credits > 0 && credits <= 2 && (
          <button data-testid="low-credit-banner" onClick={() => navigate("/paywall")}
            className="mt-4 w-full flex items-center gap-3 rounded-2xl bg-gradient-to-r from-royal to-turq text-white p-4 text-left active:scale-[0.99] transition shadow-[0_12px_28px_-14px_rgba(37,99,235,.8)]">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0"><Zap size={20} /></div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[15px]">{credits} session{credits === 1 ? "" : "s"} left</div>
              <div className="text-white/85 text-xs">Get 30 for $24.99 · replays always free</div>
            </div>
            <ArrowRight size={18} className="shrink-0" />
          </button>
        )}

        <div className="mt-7">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-navy">Quick focus</h2>
            <span className="text-xs font-semibold text-slate-500" data-testid="credits-count">{credits} session{credits === 1 ? "" : "s"} left</span>
          </div>
          <div className="grid grid-cols-4 gap-2.5">
            {FOCUS.map((f) => (
              <button key={f.id} data-testid={`focus-${f.id.replace(/\s/g, "-").toLowerCase()}`} disabled={busy}
                onClick={() => generate(f.id)}
                className="flex flex-col items-center gap-2 disabled:opacity-50 active:scale-95 transition">
                <div className={`w-full aspect-square rounded-2xl bg-gradient-to-br ${f.tint} flex items-center justify-center text-white shadow-sm`}>
                  {busy ? <Loader2 size={20} className="animate-spin" /> : <f.icon size={22} />}
                </div>
                <span className="text-[11px] font-semibold text-slate-600">{f.id}</span>
              </button>
            ))}
          </div>
        </div>

        <button data-testid="home-view-library" onClick={() => navigate("/library")}
          className="mt-6 w-full flex items-center justify-center gap-2 bg-slate-100 text-navy font-semibold py-3.5 rounded-2xl active:scale-[0.98] transition">
          <Plus size={18} /> View full library ({items.length})
        </button>
      </div>
    </div>
  );
}
